import React, { useCallback, useRef } from 'react'
import { useToast } from '@sketch/toasts'
import { MovedToProjectToast } from '../../../storageItems/components/MovedToasts'
import { useTransferStorageItems } from '../../../storageItems'
import { ProjectFragment, StorageItemFragment } from '@sketch/gql-types'

interface UseTransferShareProps {
  workspaceId: string
  newProject: ProjectFragment
}

/**
 * Hook to transfer a storage item (share or folder) to a new folder
 */
export const useTransfer = (props: UseTransferShareProps) => {
  const { showToast } = useToast()
  const propsRef = useRef(props)
  propsRef.current = props

  const [moveRaw, { loading }] = useTransferStorageItems({
    onError: errorMessage => {
      showToast(errorMessage, 'negative')
    },
  })

  const move = useCallback(
    (
      storageItem: StorageItemFragment | ProjectFragment,
      onCompleted?: () => void
    ) => {
      const { newProject, workspaceId } = propsRef.current

      const isShare = storageItem.__typename === 'Share'

      return moveRaw({
        onCompleted: () => {
          showToast(
            <MovedToProjectToast
              project={newProject}
              type={isShare ? 'Document' : 'Folder'}
              workspaceId={workspaceId}
            />
          )
          onCompleted?.()
        },
        items: [storageItem],
        location: {
          source: {
            workspaceId,
            projectId: isShare
              ? storageItem?.project?.identifier
              : storageItem.parentProjectIdentifier || undefined,
          },
          destination: {
            workspaceId,
            projectId: newProject.identifier,
          },
        },
      })
    },
    [moveRaw, showToast]
  )

  return { move, loading }
}
